import type { z } from "zod";

/**
 * Validation with the contract's own Zod schema, on submit (plan D09).
 *
 * `safeParse`, never `parse`: a refinement the payload fails — secondary goal
 * equal to the primary, a variable week that names days, an empty reminder
 * week — comes back as a FIELD ERROR the form can put under a control, never
 * as an exception that tears the sheet down. The key is the issue's path
 * joined with dots (`"secondaryGoal"`, `"injuries.2"`); an issue with no path
 * (an object-level refinement) is filed under {@link FORM_ERROR}.
 *
 * The contract's messages are English and developer-facing, so a form reads
 * WHICH field failed from here and renders its own localized sentence.
 */

export const FORM_ERROR = "_form";

export type FieldErrors = Readonly<Record<string, string>>;

export type Validated<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly errors: FieldErrors; readonly ok: false };

export function validateWith<S extends z.ZodType>(
  schema: S,
  payload: unknown
): Validated<z.output<S>> {
  const result = schema.safeParse(payload);
  if (result.success) {
    return { ok: true, value: result.data };
  }
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key =
      issue.path.length === 0 ? FORM_ERROR : issue.path.map(String).join(".");
    errors[key] ??= issue.message;
  }
  return { errors, ok: false };
}

/** Whether any error sits on `field` or below it (`"injuries"` matches
 *  `"injuries.2"`). */
export function hasErrorAt(errors: FieldErrors, field: string): boolean {
  return Object.keys(errors).some(
    (key) => key === field || key.startsWith(`${field}.`)
  );
}

export const NO_ERRORS: FieldErrors = Object.freeze({});
